import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Eye, EyeOff, X, Users, GitMerge } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { getCustomerDuplicates, bulkDeleteCustomers, bulkToggleCustomers, mergeCustomers, formatApiError } from "@/lib/api";

export function CustomerDuplicates({ token, open, onOpenChange, onChanged }) {
  const [entries, setEntries] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const d = await getCustomerDuplicates(token);
      // Flatten all duplicate groups into a single flat list.
      const flat = [];
      const seen = new Set();
      (d.groups || []).forEach((g) => g.entries.forEach((e) => {
        if (!seen.has(e.id)) { seen.add(e.id); flat.push({ ...e, _match: g.type, _value: g.value }); }
      }));
      setEntries(flat);
      setSelected(new Set());
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open]);

  const toggleOne = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = entries.length > 0 && entries.every((e) => selected.has(e.id));
  const toggleAll = () => setSelected((s) => {
    if (entries.every((e) => s.has(e.id))) return new Set();
    return new Set(entries.map((e) => e.id));
  });

  const act = async (kind) => {
    const ids = [...selected];
    if (!ids.length) return toast.error("Select at least one entry");
    if (kind === "delete" && !window.confirm(`Permanently delete ${ids.length} selected entr${ids.length === 1 ? "y" : "ies"}? This cannot be undone.`)) return;
    try {
      if (kind === "delete") await bulkDeleteCustomers(token, ids);
      else await bulkToggleCustomers(token, ids, kind === "unhide");
      toast.success(kind === "delete" ? "Deleted selected" : kind === "hide" ? "Hidden selected" : "Unhidden selected");
      onChanged?.();
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const merge = async () => {
    const ids = [...selected];
    if (ids.length < 2) return toast.error("Select at least two entries to merge");
    if (!window.confirm(`Merge ${ids.length} selected entries into one record? The best phone/place/remark are kept and the extra entries are removed. This cannot be undone.`)) return;
    try {
      const res = await mergeCustomers(token, ids);
      toast.success(`Merged into "${res.merged?.name}" · ${res.removed} extra removed`);
      onChanged?.();
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border border-white/10 text-white sm:max-w-3xl rounded-none flex flex-col max-h-[88dvh]" data-testid="duplicates-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-2xl tracking-tight text-white flex items-center gap-2"><Users className="h-5 w-5" /> Duplicate Entries</DialogTitle>
          <DialogDescription className="text-white/50">Same phone number, or (when no phone) the exact same name, are treated as duplicates.</DialogDescription>
        </DialogHeader>

        {/* Action bar */}
        <div className="flex flex-wrap items-center gap-2 border border-white/10 bg-[#141414] px-3 py-2 shrink-0" data-testid="duplicates-action-bar">
          <span className="text-xs text-white/60" data-testid="duplicates-selected-count">{selected.size} selected</span>
          <div className="flex-1" />
          <Button onClick={merge} disabled={selected.size < 2} variant="outline" className="rounded-none h-8 border-emerald-500/40 bg-transparent text-emerald-400 hover:bg-emerald-500 hover:text-white text-xs uppercase tracking-wider disabled:opacity-30" data-testid="duplicates-merge"><GitMerge className="h-3.5 w-3.5 mr-1" /> Merge</Button>
          <Button onClick={() => act("delete")} disabled={!selected.size} variant="outline" className="rounded-none h-8 border-red-500/40 bg-transparent text-red-400 hover:bg-red-500 hover:text-white text-xs uppercase tracking-wider disabled:opacity-30" data-testid="duplicates-bulk-delete"><Trash2 className="h-3.5 w-3.5 mr-1" /> Delete</Button>
          <Button onClick={() => act("hide")} disabled={!selected.size} variant="outline" className="rounded-none h-8 border-white/20 bg-transparent text-white hover:bg-white hover:text-black text-xs uppercase tracking-wider disabled:opacity-30" data-testid="duplicates-bulk-hide"><EyeOff className="h-3.5 w-3.5 mr-1" /> Hide</Button>
          <Button onClick={() => act("unhide")} disabled={!selected.size} variant="outline" className="rounded-none h-8 border-white/20 bg-transparent text-white hover:bg-white hover:text-black text-xs uppercase tracking-wider disabled:opacity-30" data-testid="duplicates-bulk-unhide"><Eye className="h-3.5 w-3.5 mr-1" /> Unhide</Button>
        </div>

        {/* Global Select All */}
        {!loading && entries.length > 0 && (
          <label className="flex items-center gap-2 px-3 py-2 border border-white/10 bg-[#0F0F0F] shrink-0 cursor-pointer" data-testid="duplicates-selectall-row">
            <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="duplicates-select-all" className="accent-white h-4 w-4" />
            <span className="text-[11px] uppercase tracking-wider text-white/60">Select all ({entries.length})</span>
          </label>
        )}

        <div className="flex-1 overflow-auto border border-white/10">
          {loading && <p className="text-sm text-white/40 py-8 text-center">Scanning…</p>}
          {!loading && entries.length === 0 && <p className="text-sm text-white/40 py-10 text-center" data-testid="duplicates-empty">No duplicate entries found. 🎉</p>}
          {!loading && entries.map((c) => (
            <div key={c.id} className={`flex items-center gap-3 px-3 py-2.5 text-sm border-t border-white/5 first:border-t-0 ${c.active === false ? "opacity-50" : ""} ${selected.has(c.id) ? "bg-white/5" : ""}`} data-testid={`duplicate-entry-${c.id}`}>
              <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggleOne(c.id)} data-testid={`duplicate-select-${c.id}`} className="accent-white h-4 w-4 shrink-0" />
              <span className="flex-1 min-w-0 truncate text-white">{c.name}{c.active === false && <span className="ml-2 text-[10px] uppercase text-white/40">Hidden</span>}</span>
              <span className="w-28 truncate text-white/60">{c.phone || "—"}</span>
              <span className="w-24 truncate text-white/60">{c.place || "—"}</span>
              <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-purple-500/40 bg-purple-500/10 text-purple-300 shrink-0">{c._match === "phone" ? "Phone" : "Name"}</span>
            </div>
          ))}
        </div>

        <Button onClick={() => onOpenChange(false)} className="shrink-0 rounded-none h-10 bg-white text-black hover:bg-white/90 text-xs uppercase tracking-wider" data-testid="duplicates-close"><X className="h-4 w-4 mr-1" /> Close</Button>
      </DialogContent>
    </Dialog>
  );
}
